import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { debounce } from 'lodash';
import { Context } from 'components/Store';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import PAGINATION_SIZE, {
  GET_ALL_PRODUCTS_IN_ORG
} from 'helpers/globalConstants';
import { inventoryProduct as productApi } from 'utilities/api';
import axios from 'axios';
import { getAccessToken } from 'utilities/base-auth';
import {
  CROPWISE_PROXY_URL,
  CROPWISE_PROXY_V2_URL
} from 'utilities/apiConstants';
import useInventoryProductsV2Flag from 'hooks/useInventoryProductsV2Flag';
import filteredProducts from 'screens/ProductList/helpers/filteredProducts';

const useProducts = defaultParams => {
  const [{ organization }, dispatch] = useContext(Context);
  const useV2Products = useInventoryProductsV2Flag();
  const productsBaseUrl = useV2Products
    ? `${CROPWISE_PROXY_V2_URL}/inventory/products`
    : `${CROPWISE_PROXY_URL}/v1/inventory/products`;
  const productsBaseUrlRef = useRef(productsBaseUrl);
  const orgIdRef = useRef(organization.id);
  const cancelTokenSourceRef = useRef(null);
  const [allProducts, setAllProducts] = useState([]);
  const [isLoadingAllProducts, setIsLoadingAllProducts] = useState(false);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [productsCount, setProductsCount] = useState(0);
  const [pageNo, setPageNo] = useState(0);
  const [request, setRequest] = useState({
    pageNo,
    size: PAGINATION_SIZE,
    ...defaultParams
  });
  const [selectedProduct, setSelectedProduct] = useState(null);
  const emptySelectedProduct = [];

  useEffect(() => {
    orgIdRef.current = organization.id;
  }, [organization]);

  useEffect(() => {
    productsBaseUrlRef.current = productsBaseUrl;
  }, [productsBaseUrl]);

  let activeRequests = 0; // Counter to track the number of active requests

  const getProducts = async params => {
    activeRequests += 1; // Increment the active requests counter
    setIsLoading(true);

    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel(
        'Request canceled due to a new request.'
      );
    }

    cancelTokenSourceRef.current = axios.CancelToken.source();

    const config = {
      ...request,
      ...params
    };

    const headers = {
      'Content-Type': 'application/json',
      'cwf-context': JSON.stringify({
        organization_id: orgIdRef.current
      }),
      Authorization: `Bearer ${getAccessToken()}`
    };

    try {
      const { data } = await axios.get(productsBaseUrlRef.current, {
        headers,
        params: config,
        cancelToken: cancelTokenSourceRef.current.token
      });

      if (config?.searchBy?.length) {
        setProducts(
          filteredProducts(data.results, config?.searchBy, emptySelectedProduct)
        );
        setProductsCount(data.totalElements);
      } else {
        setProducts(data.results);
        setProductsCount(data.totalElements);
      }
    } catch (err) {
      if (!axios.isCancel(err)) {
        parseServerError(dispatch)(err);
      }
    } finally {
      activeRequests -= 1; // Decrement the active requests counter
      if (activeRequests === 0) {
        setIsLoading(false); // Only set to false if no active requests remain
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGetProducts = useCallback(
    debounce(params => {
      getProducts(params);
    }, 300), // Adjust debounce time as needed (300ms in this case)
    []
  );

  // Cancel the trailing debounce on unmount: it otherwise fires up to 300ms
  // later, fetching and setting state against an unmounted component.
  useEffect(() => () => debouncedGetProducts.cancel(), [debouncedGetProducts]);

  const getAllUseProducts = async () => {
    setIsLoadingAllProducts(true);
    const promise = axios.get(productsBaseUrlRef.current, {
      headers: {
        'cwf-context': JSON.stringify({
          organization_id: organization.id
        }),
        Authorization: `Bearer ${getAccessToken()}`
      },
      params: {
        size: GET_ALL_PRODUCTS_IN_ORG
      }
    });

    await promise
      .then(({ data }) => {
        setAllProducts(data.results);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => {
        setIsLoadingAllProducts(false);
      });
  };

  const getProduct = async productId => {
    setIsLoading(true);
    const { promise } = await productApi.fetch(productId);

    await promise
      .then(({ data }) => {
        setSelectedProduct(data);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const reload = useCallback(async () => {
    debouncedGetProducts(request);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const reset = () => {
    setRequest({
      ...request,
      pageNo,
      totalPages: 0,
      size: PAGINATION_SIZE
    });
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization, request]);

  const filterByNameQuery = useCallback(
    newQuery => {
      setRequest(r =>
        r.searchBy === newQuery ? r : { ...r, pageNo: 0, searchBy: newQuery }
      );
    },
    [setRequest]
  );

  const filterByProductTypes = useCallback(
    newProductTypes => {
      setRequest(r => ({
        ...r,
        pageNo: 0,
        productTypes: Array.isArray(newProductTypes)
          ? newProductTypes.join(',') || undefined
          : newProductTypes
      }));
    },
    [setRequest]
  );

  const filterBySources = useCallback(
    newSources => {
      setRequest(r => ({
        ...r,
        pageNo: 0,
        sources: Array.isArray(newSources)
          ? newSources.join(',') || undefined
          : newSources
      }));
    },
    [setRequest]
  );

  const goToPage = useCallback(
    (newPage, params) => {
      setPageNo(newPage);
      setRequest(r => ({ ...r, ...params, pageNo: newPage }));
    },
    [setRequest]
  );

  const sort = useCallback(
    (field, dir) => {
      setRequest(r => ({ ...r, pageNo: 0, sortBy: field, sortDir: dir }));
    },
    [setRequest]
  );

  return {
    getProducts: debouncedGetProducts, // Return the debounced version
    products,
    productsCount,
    pageNo: request.pageNo,
    pageSize: request.size,
    goToPage,
    isLoading,
    reset,
    reload,
    filterByNameQuery,
    filterByProductTypes,
    filterBySources,
    sort,
    sortBy: request.sortBy,
    sortDir: request.sortDir,
    searchBy: request.searchBy,
    productTypes: request.productTypes,
    sources: request.sources,
    getAllUseProducts,
    allProducts,
    isLoadingAllProducts,
    getProduct,
    selectedProduct
  };
};

export default useProducts;
